import React, { FC, useContext, useEffect } from 'react';
import { generateName } from './lib';
import { AdvertContext } from './AdvertProviderDfp';
import { AdSlot, AdWrapper, Placeholder, ReportButton } from './Styles';
import {
    Size,
    Breakpoint,
    ZPG_DFP_NETWORK,
    DFP_AD_UNIT,
    ACTION_TYPE,
    CustomWindow,
    AD_UNIT_ROOTS,
    AD_UNIT_PAGES
} from './types';

declare const window: CustomWindow;

export type AdvertSlotDfpProps = {
    sizes: Size[];
    showOnly?: Breakpoint;
    position: number;
    adUnitCode?:
        | DFP_AD_UNIT
        | [AD_UNIT_ROOTS, AD_UNIT_PAGES]
        | [AD_UNIT_ROOTS, AD_UNIT_PAGES, string];
    dfpNetworkId?: ZPG_DFP_NETWORK.ID;
    collapseEmpty?: boolean;
};

export const AdvertSlotDfp: FC<AdvertSlotDfpProps> = ({
    sizes,
    showOnly,
    position,
    adUnitCode: adUnitCodeFromProps,
    dfpNetworkId: dfpNetworkIdFromProps,
    collapseEmpty,
    ...passThroughProps
}) => {
    const { state, dispatch, mountSlot, unmountSlot } = useContext(
        AdvertContext
    );
    const {
        adUnitCode: adUnitCodeFromContext,
        dfpNetworkId: dfpNetworkIdFromContext,
        showPlaceHolderContent,
        debugMode,
        dfpInitialised,
        reportMode,
        slots
    } = state;

    const isDev = process.env.NODE_ENV !== 'production';

    const size = sizes[0];

    const id = generateName({ size, position });
    const { isDefined: slotHasBeenDefined } = slots[id] || {};

    const height = typeof size !== 'string' ? size[1] : undefined;
    const [showOnlyDirection, showOnlyWidth] = showOnly || [];

    const adUnitCode = adUnitCodeFromProps || adUnitCodeFromContext;

    const adUnit = Array.isArray(adUnitCode)
        ? adUnitCode.length === 2
            ? `${adUnitCode[0]}${adUnitCode[1]}`
            : `${adUnitCode[0]}${adUnitCode[1]}${adUnitCode[2]}`
        : adUnitCode;

    const adUnitPath =
        (dfpNetworkIdFromProps || dfpNetworkIdFromContext) + adUnit;

    const hideFromSizeDown =
        showOnly && showOnlyDirection === '>' ? showOnlyWidth : undefined;
    const hideFromSizeUp =
        showOnly && showOnlyDirection === '<' ? showOnlyWidth : undefined;

    const log = (...args: unknown[]): void => {
        if (debugMode) {
            console.log(`AdSlot(${id})`, ...args);
        }
    };

    // only display once slot has been defined in DfpManager via MOUNT_SLOT
    useEffect(() => {
        if (dfpInitialised && slotHasBeenDefined) {
            const windowRef = window;
            log('display()');
            windowRef.googletag = windowRef.googletag || {
                cmd: []
            };
            windowRef.googletag.cmd.push(function () {
                windowRef.googletag.display(id);
            });
        }
    }, [dfpInitialised, slotHasBeenDefined]);

    // only execute once on client side mount and re-mount if the id updates
    useEffect(() => {
        log('mountSlot');
        mountSlot({
            id,
            sizes,
            hideFromSizeDown,
            hideFromSizeUp,
            position,
            adUnitPath,
            collapseEmpty
        });
        return () => {
            log('unmountSlot');
            unmountSlot({ id });
        };
    }, [id]);

    return (
        <AdWrapper height={height} breakpoint={showOnly} {...passThroughProps}>
            {reportMode && (
                <ReportButton
                    data-testid="report_ad"
                    onClick={() =>
                        dispatch({
                            type: ACTION_TYPE.REPORT_SLOT,
                            payload: {
                                id,
                                position
                            }
                        })
                    }
                >
                    <p>Click to report this ad</p>
                </ReportButton>
            )}
            <AdSlot
                itemScope={true}
                itemType="https://schema.org/WPAdBlock"
                id={id}
                height={height}
                data-testid={id}
                key={id}
            >
                {isDev && showPlaceHolderContent && (
                    <Placeholder breakpoint={showOnly}>
                        {JSON.stringify(
                            {
                                id,
                                sizes,
                                position,
                                hideFromSizeDown,
                                hideFromSizeUp
                            },
                            null,
                            1
                        )}
                    </Placeholder>
                )}
            </AdSlot>
        </AdWrapper>
    );
};
